import { ApiPermission, GetUsersParams, UserType } from "@bvhi/backend-api";
import { Card, Stack } from "@bvhi/ui-base";
import { Suspense, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { BVHINavigationCreateEntityButton } from "../../../../../components/bvhi-navigation-create-entity-button.tsx";
import { NavigationBar } from "../../../../../components/navigation-bar.tsx";
import { useAuthContext } from "../../../../../context/use-auth-context.tsx";
import { useBVHIDataGridFilter } from "../../../../../hooks/use-bvhi-datagrid-Filter.ts";
import { Route } from "../../../../../routes/_main/app/users.index";
import { UsersDataGrid } from "./components/datagrid/users-datagrid";
import { defaultUsersFilter } from "./components/default-users-filter.ts";
import { UsersErrorAlerts } from "./components/users-error-alerts.tsx";
import { UsersFilterCard } from "./components/users-filter-card";
import { UsersMarketingConsentCard } from "./components/users-marketing-consent-card.tsx";
import { useUsersSortParams } from "./hooks/use-users-sort-params";
import { UsersDownloadCard } from "./components/datagrid/users-download-card.tsx";
import { usePermissions } from "../../../../../hooks/use-permissions.tsx";

export const UsersListView = () => {
  const searchParams = Route.useSearch();

  const { t } = useTranslation(["users"]);
  const { currentUser } = useAuthContext();
  const { getSortConfig } = useUsersSortParams();
  const { hasPermissionTo } = usePermissions();
  const { resetFilter, handleFilterChange, handleDataGridChange, requestParams } = useBVHIDataGridFilter<GetUsersParams>(
    defaultUsersFilter,
    Route.fullPath,
    getSortConfig,
    searchParams
  );

  const hasPermissionToDownloadUsers = useMemo(() => {
    return hasPermissionTo([ApiPermission["user:download"]]);
  }, [hasPermissionTo]);

  return (
    <>
      <NavigationBar
        actions={[<BVHINavigationCreateEntityButton key={0} entity={t("users:users.user")} navigateTarget={{ to: "/app/users/create" }} />]}
        showBreadcrumbs={true}
      />

      <Stack pt={2} spacing={2}>
        <UsersErrorAlerts />

        <UsersFilterCard filter={requestParams} onChange={handleFilterChange} onReset={resetFilter} />

        <Suspense fallback={<Card sx={{ height: 300, minHeight: 300 }} />}>
          <UsersDataGrid updateCallback={handleDataGridChange} />
        </Suspense>

        {currentUser?.type === UserType.ADMIN && <UsersMarketingConsentCard />}
        {currentUser?.type === UserType.ADMIN && hasPermissionToDownloadUsers && <UsersDownloadCard />}
      </Stack>
    </>
  );
};
